'use client';
import { getLinkProps } from '@/app/lib';
import { getImageBuilder } from '@/app/lib/media-helper';
import Link from 'next/link';
import { cva } from 'class-variance-authority';
import { AppImage, Typography } from '@workspace/ui/common';
import { theme } from '@workspace/ui/theme';
import type { IHeaderData } from '../interfaces';

const logoVariants = cva('relative', {
  variants: {
    theme: {
      porta: 'aspect-[21/5] h-6 sm:h-7.5',
      portaNZ: 'aspect-[21/5] h-6 sm:h-7.5',
      redRec: 'aspect-[7/2] w-32',
    },
  },
  defaultVariants: {
    theme: 'porta',
  },
});

interface HeaderLogoProps {
  headerData?: IHeaderData | null;
}

export const Logo = ({ headerData }: HeaderLogoProps) => {
  if (!headerData?.headerLogo) return null;
  const { headerLogo } = headerData;
  const logoSource = headerLogo.bynderLogo || headerLogo.imageUrl;

  const linkProps = getLinkProps(headerLogo.link);
  const logoAlt = headerLogo.bynderLogo?.alt || headerLogo.title || 'Logo';

  // Ensure href is always defined for Next.js Link
  if (!linkProps.href) {
    return null;
  }

  const logoWidth = theme === 'redRec' ? 128 : 126;
  const logoSrc = logoSource ? getImageBuilder(logoSource).width(logoWidth).dpr(1).url() : '';

  return (
    <Link {...linkProps} className="flex items-center mr-25">
      {logoSource ? (
        <div className={logoVariants({ theme })}>
          <AppImage
            src={logoSrc}
            alt={logoAlt}
            className="cursor-pointer"
            quality={100}
            fill
            sizes={`${logoWidth}px`}
          />
        </div>
      ) : (
        <Typography variant="h6" as="h1" className="text-primary-500 font-bold">
          {headerLogo.title}
        </Typography>
      )}
    </Link>
  );
};
